import React, { useState, useRef, useEffect } from "react";
import aiImg from "../assets/ai-model.jpg";
import Aivideo from "../assets/ai-video.mp4";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMicrophone, faStop } from "@fortawesome/free-solid-svg-icons";
import UseAnitaChat from "../utils/use-anita-chat";
import UseAuth from "../utils/use-auth";
import axiosInstance from "../utils/axios-instance";
import Speak from "../utils/speak";
import { FaPaperPlane, FaSpinner, FaTimes, FaFilePdf, FaFileWord } from "react-icons/fa";
import { AiOutlinePaperClip } from "react-icons/ai";
import { RiRobot2Fill, RiUserFill } from "react-icons/ri";
import { jsPDF } from 'jspdf';
import { marked } from "marked";
import { ExtractDocumentText } from "../utils/extract-document-text";

const ChatVoiceInterview = () => {
  const { user } = UseAuth();
  const { currentConversation, handleSelectConversation } = UseAnitaChat();
  const [messages, setMessages] = useState({ totalCount: 0, data: [] });
  const [loading, setLoading] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState(""); // For real-time display
  const recognitionRef = useRef(null);
  const [timeLeft, setTimeLeft] = useState(900); // 15 minutes (900 seconds)
  const [interviewActive, setInterviewActive] = useState(false);
  const [messageFromChat, setMessageFromChat] = useState("");
  const messagesEndRef = useRef(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const containerRef = useRef(null);
  // Added flag to check if interview has been started
  const [interviewStarted, setInterviewStarted] = useState(false);

  const [loadingState, setLoadingState] = useState(null);
  const[imageBase64, setImageBase64]  = useState(null);
  const [extractedText, setExtractedText] = useState(null);
  const [fileType, setFileType] = useState(null); // Track file type: "image", "pdf", "doc"
  const [fileName, setFileName] = useState(""); // Track file name

  // Store conversation ID in ref to ensure it persists between renders
  // and doesn't trigger re-renders when updated
  const conversationIdRef = useRef(null);

  // Keep track of previous conversation ID to detect changes
  const prevConversationIdRef = useRef(currentConversation?.id);

  // New state for enhanced speech recognition
  const [isListening, setIsListening] = useState(false);
  const [finalTranscript, setFinalTranscript] = useState("");
  const [currentSpeechText, setCurrentSpeechText] = useState(""); // Track only current speech session
  // Removed silenceTimeoutRef since we're not using automatic timeout

  // ------------------------------------------- DETECT CONVERSATION RESET -------------------------------------------
  useEffect(() => {
    // Check if conversation was set to null (reset triggered from sidebar)
    const wasResetTriggered =
      prevConversationIdRef.current && !currentConversation?.id;

    if (wasResetTriggered) {
      // console.log("Conversation reset detected! Resetting all states...");

      // Reset all component states
      setMessages({ totalCount: 0, data: [] });
      setLoading(false);
      setSpeaking(false);
      setRecording(false);
      setTranscript("");
      setInterimTranscript("");
      setFinalTranscript("");
      setInterimTranscript("");
      setCurrentSpeechText("");
      setTimeLeft(900);
      setInterviewActive(false);
      setMessageFromChat("");
      setInterviewStarted(false);
      setIsListening(false);

      // Clear the conversation ID reference
      conversationIdRef.current = null;

      // Clear the loaded conversations set
      if (loadedConversationsRef.current) {
        loadedConversationsRef.current.clear();
      }

      // If speech recognition is running, stop it
      if (recognitionRef.current && recording) {
        recognitionRef.current.abort();
      }
    }

    // Check if conversation ID changed (user selected a different conversation)
    if (prevConversationIdRef.current !== currentConversation?.id && currentConversation?.id) {
      // console.log("Conversation changed! Updating messages...");
      // We'll fetch messages for the new conversation
      // Make sure we don't have stale messages displayed while fetching
      setMessages({ totalCount: 0, data: [] });
    }

    // Update the previous conversation ID reference for next comparison
    prevConversationIdRef.current = currentConversation?.id;
  }, [currentConversation, recording]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ------------------------------------------- USE EFFECT FOR MESSAGES -------------------------------------------
  // Keep track of whether we've loaded messages for this conversation
  const loadedConversationsRef = useRef(new Set());

  useEffect(() => {
    const findMessages = async () => {
      try {
        // Always use the ref first, then fall back to context
        const conversationId =
          conversationIdRef.current || currentConversation?.id;

        if (!conversationId) {
          // console.log("No conversation ID available, not fetching messages");
          return;
        }

        // Check if we've already loaded messages for this conversation
        if (loadedConversationsRef.current.has(conversationId)) {
          // console.log(`Messages already loaded for conversation: ${conversationId}`);
          return;
        }

        const res = await axiosInstance(
          `/voice-chat/find/messages/${conversationId}`
        );

        // Set messages without duplicating
        setMessages(res.data);

        // Mark this conversation as loaded
        loadedConversationsRef.current.add(conversationId);
      } catch (error) {
        // console.log("Error fetching messages:", error);
      }
    };

    // Only fetch if we have a conversation ID
    if (conversationIdRef.current || currentConversation?.id) {
      findMessages();
    }
  }, [currentConversation?.id]);


  //-------------PARSE MESSAGE TO DOWNLOAD -------------------------
  const parseMessageWithDownloads = (message) => {
    // Regular expression to match content between download start and end flags
    const downloadPattern = /--download start\s*([\s\S]*?)\s*--download end/g;


    let result = [];
    let lastIndex = 0;
    let match;

    // Find all download sections
    while ((match = downloadPattern.exec(message)) !== null) {
      // Add text before the download section
      if (match.index > lastIndex) {
        result.push({
          type: 'text',
          content: message.substring(lastIndex, match.index)
        });
      }

      // Add the download section
      result.push({
        type: 'download',
        content: match[1].trim()
      });

      lastIndex = match.index + match[0].length;
    }

    // Add any remaining text after the last download section
    if (lastIndex < message.length) {
      result.push({
        type: 'text',
        content: message.substring(lastIndex)
      });
    }

    return result;
  };

  // Function to generate and download PDF
  const handleDownload = (content, filename = 'download.pdf') => {
    // Create new PDF document
    const doc = new jsPDF();

    // Add the content to the PDF
    doc.text(content, 10, 10);

    // Save the PDF
    doc.save(filename);
  };

  const filterTextFromDownloadbaleContent = (input) => {
    //this is implemented so the voice asistant does not read the downloadable content
    return input.replace(/--download start[\s\S]*?--download end/g, '').trim();
  }

  // ------------------------------------------- START CONVERSATION -------------------------------------------
  const startConversation = async () => {
    if (!speaking && !interviewStarted) {
      setSpeaking(true);
      setInterviewActive(true);
      setInterviewStarted(true);

      // This message has download content: --download start \nThis is important information that will be downloaded as a PDF file.\n--download end
      const introMessage = `Hello, Welcome to the Martech Interview Simulator We are here to simulate real-life interview experiences for marketing technology roles. Before we begin ✅ Find a quiet, comfortable space. ✅ Use a headset or speak clearly for better communication. Ready to begin? Just say Proceed, and we will start your Interview. 
      `;

      // Generate unique message ID to prevent duplicates
      const introMessageId = 'intro-' + Date.now();

      // Check if intro message already exists to prevent duplication
      setMessages((prevMessages) => {
        // Check if an intro message already exists
        const introExists = prevMessages.data.some(
          msg => msg.userId === null && msg.message.includes("Welcome to the Martech Interview Simulator")
        );

        if (introExists) {
          return prevMessages;
        }


        return {
          totalCount: prevMessages.totalCount + 1,
          data: [...prevMessages.data, {
            message: introMessage,
            userId: null,
            id: introMessageId
          }],
        };
      });

      const filteredIntroMessage = filterTextFromDownloadbaleContent(introMessage);

      await Speak(filteredIntroMessage, () => {
        setSpeaking(false);
        // Don't automatically start listening - let user choose when to speak
      });
    }
  };


  // ------------------------------------------- SEND MESSAGE -------------------------------------------
  const sendMesageFromChat = (msg) => {
    sendMessage(msg);
    setMessageFromChat("");
  };

  const sendMessage = async (msg) => {
    if (!msg.trim()) return;

    setLoading(true);

    // Always send the message with the ref's conversation ID if available
    const message = {
      conversationId: conversationIdRef.current || currentConversation?.id,
      message: msg,
      userId: user?.id,
    };

    // Generate a temporary ID for this message to prevent duplicates
    const tempMessageId = Date.now().toString();
    message.tempId = tempMessageId;

    // Add user message to UI immediately
    setMessages((prevMessages) => {
      return {
        totalCount: prevMessages.totalCount + 1,
        data: [...prevMessages.data, message],
      };
    });

    // Clear file attachments and transcripts
    setFileType(null);
    setFileName("");
    setExtractedText(null);
    setImageBase64(null);
    setFinalTranscript("");
    setInterimTranscript("");

    try {
      // Send the message to the API
      const res = await axiosInstance.post(`/voice-chat/send/message`, {
        ...message,
        messages: messages?.data,
        extraPrompt: extractedText ? `${extractedText}\n\n${msg}` : null
      });

      
      // Check if this is our first message (no conversation ID yet)
      if (
        !conversationIdRef.current &&
        res.data.conversation &&
        res.data.conversation.id
      ) {
        // Store the conversation ID in our ref
        conversationIdRef.current = res.data.conversation.id;

        // Also update the context if the handleSelectConversation function is available
        if (handleSelectConversation) {
          handleSelectConversation(res.data.conversation);
        }
      }

      // Start speaking the response
      setSpeaking(true);
      await Speak(res?.data?.message?.message, afterSpeech);

      // Add AI response to messages, avoiding duplicates
      setMessages((prev) => {
        // Check if this AI response already exists to avoid duplicates
        const isDuplicate = prev.data.some(
          m => m.message === res.data.message.message && m.userId === null
        );

        if (isDuplicate) {
          return prev;
        }

        return {
          totalCount: prev.totalCount + 1,
          data: [...prev.data, res.data.message],
        };
      });
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setLoading(false);
    }
  };

  const afterSpeech = () => {
    setSpeaking(false);
    // Don't automatically start listening - let user decide when to speak
  };

  // ----------------------------------------- ENHANCED SPEECH RECOGNITION -----------------------------------------
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) return;

    // Only create and start recognition if we should be listening
    if (isListening) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onstart = () => {
        console.log('Speech recognition started');
        setRecording(true);
      };

      recognitionRef.current.onresult = (event) => {
        // Only process if we're still supposed to be listening
        if (!isListening) return;

        let fullTranscript = '';
        let interimText = '';

        // Get all final results
        for (let i = 0; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            fullTranscript += event.results[i][0].transcript + ' ';
          }
        }

        // Get interim result from the last result
        if (event.results.length > 0) {
          const lastResult = event.results[event.results.length - 1];
          if (!lastResult.isFinal) {
            interimText = lastResult[0].transcript;
          }
        }

        // Update states
        setFinalTranscript(fullTranscript.trim());
        setInterimTranscript(interimText);

        // Update current speech text for this session
        const currentText = (fullTranscript + interimText).trim();
        setCurrentSpeechText(currentText);

        // Update textarea - just set it to the current speech text
        setMessageFromChat(currentText);
      };

      recognitionRef.current.onend = () => {
        console.log('Speech recognition ended, isListening:', isListening);
        
        // Only restart if we're still supposed to be listening
        if (isListening) {
          try {
            console.log('Restarting speech recognition...');
            recognitionRef.current.start();
          } catch (error) {
            console.error('Error restarting:', error);
            setIsListening(false);
            setRecording(false);
          }
        } else {
          setRecording(false);
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        
        if (event.error === 'aborted') {
          console.log('Speech recognition aborted (expected)');
          return;
        }
        
        // For other errors, stop listening
        setIsListening(false);
        setRecording(false);
      };

      try {
        console.log('Starting speech recognition...');
        recognitionRef.current.start();
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        setIsListening(false);
        setRecording(false);
      }
    }

    // Cleanup function - always run when effect cleans up
    return () => {
      if (recognitionRef.current) {
        console.log('Cleaning up speech recognition');
        try {
          recognitionRef.current.abort();
        } catch (error) {
          console.log('Error during cleanup:', error);
        }
        recognitionRef.current = null;
      }
    };
  }, [isListening]); // Only depend on isListening

  // Start continuous listening
  const startListening = () => {
    if (!speaking && !isListening) {
      console.log('User clicked Start Speaking');
      setIsListening(true);
      setFinalTranscript('');
      setInterimTranscript('');
      setCurrentSpeechText('');
      setMessageFromChat(''); // Clear the textarea when starting fresh
    }
  };

  // Stop listening manually
  const stopListening = () => {
    console.log('User clicked Stop');
    
    // First set the state to stop listening
    setIsListening(false);
    setRecording(false);
    
    // Then abort the recognition
    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
      } catch (error) {
        console.log('Error aborting recognition:', error);
      }
    }
    
    // Clean up speech states
    setFinalTranscript('');
    setInterimTranscript('');
    setCurrentSpeechText('');
  };

  // Legacy toggle function for backward compatibility
  const toggleRecording = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleFile = async (e) => {
    // Prevent file handling when speaking
    if (speaking) {
      return;
    }
    
    const file = e.target.files[0];

    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/pdf', // PDF
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' // DOC, DOCX
    ];

    if (!validTypes.includes(file.type)) {
      alert("Only PDF, DOC, and DOCX files are allowed.");
      return;
    }

    setFileName(file.name);

    if (file.type === 'application/pdf') {
      // Handle PDF files
      setFileType("pdf");
      setLoadingState("processing-pdf");

      // Create a preview icon for PDF
      setImageBase64("pdf"); // Using a marker to indicate it's a PDF file

      // Extract text from PDF
      const text = await extractPdfText(file);
      setLoadingState(null);
      setExtractedText(text)

      // Send the extracted text
      // await handleSendMessage(text);
    }
    else if (file.type === 'application/msword' ||
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      // Handle DOC/DOCX files
      setFileType("doc");
      setLoadingState("processing-document");

      // Create a preview icon for DOC/DOCX
      setImageBase64("doc"); // Using a marker to indicate it's a DOC file

      // Use existing document text extraction function
      const text = await ExtractDocumentText(file);
      setLoadingState(null);
      setExtractedText(text)
    }
  };

  const extractPdfText = async (file) => {
    try {
      // Use any PDF extraction library or API you have access to
      // For this example, we'll assume ExtractDocumentText can handle PDFs
      const text = await ExtractDocumentText(file);
      return text;
    } catch (error) {
      console.error("Error extracting PDF text:", error);
      return "Error extracting text from PDF.";
    }
  };

  // Clear file attachment
  const clearFileAttachment = () => {
    setImageBase64(null);
    setFileType(null);
    setFileName("");
    setExtractedText(null);
  };

  // Render file preview based on file type
  const renderFilePreview = (fileData, fileType, name) => {
    if (fileType === "image" || !fileType) {
      // For backward compatibility with existing image previews
      return (
        <img src={fileData} alt="Preview" className="max-h-36 object-contain" />
      );
    } else if (fileType === "pdf" || fileData === "pdf") {
      return (
        <div className="flex items-center p-2 bg-red-50 rounded-md">
          <FaFilePdf className="text-red-500 text-2xl mr-2" />
          <span className="text-sm truncate max-w-[150px]">{name || "PDF Document"}</span>
        </div>
      );
    } else if (fileType === "doc" || fileData === "doc") {
      return (
        <div className="flex items-center p-2 bg-blue-50 rounded-md">
          <FaFileWord className="text-blue-500 text-2xl mr-2" />
          <span className="text-sm truncate max-w-[150px]">{name || "Word Document"}</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col h-full bg-[#0f1829] overflow-hidden">
      {/* Header */}
      <div className="border-b border-white/10 bg-[#111d42] px-5 py-4 flex-shrink-0">
        <h2 className="ps-8 lg:ps-0 text-xl font-bold text-white">Anita — Interview Simulator</h2>
        <p className="ps-8 lg:ps-0 text-sm text-white/40">MarTech Interview Practice</p>
      </div>

      {/* Main content: video panel + chat panel */}
      <div className="flex-1 grid grid-rows-[auto_1fr] lg:grid-rows-1 lg:grid-cols-[300px_1fr] overflow-hidden">

        {/* ── Left: Video / Controls ── */}
        <div className="p-3 lg:h-full flex-shrink-0">
          <div className="bg-[#111d42] border border-white/10 rounded-xl flex flex-col h-full overflow-hidden">
            {/* Video */}
            <div className="w-full h-40 sm:h-48 lg:flex-1 overflow-hidden rounded-t-xl">
              {speaking ? (
                <video src={Aivideo} className="w-full h-full object-cover" loop autoPlay muted />
              ) : (
                <img src={aiImg} className="w-full h-full object-contain" alt="AI assistant" />
              )}
            </div>

            {/* Controls */}
            <div className="p-3 border-t border-white/10 flex flex-col gap-2 flex-shrink-0">
              <div className="flex gap-2">
                <button
                  className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold bg-[#1B2B5E] border border-white/15 hover:border-[#F05A22]/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  onClick={startConversation}
                  disabled={speaking || isListening || interviewStarted}
                >
                  {interviewStarted ? "Started ✓" : "Start Session"}
                </button>

                <button
                  className={`flex-1 py-2.5 rounded-xl text-white text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed border ${
                    isListening
                      ? "bg-red-600/90 border-red-500/50 hover:bg-red-600"
                      : "bg-[#1B2B5E] border-white/15 hover:border-[#F05A22]/40"
                  }`}
                  onClick={isListening ? stopListening : startListening}
                  disabled={loading || speaking || !interviewStarted}
                >
                  {loading ? "Wait..." : speaking ? "Speaking..." : isListening ? "Stop" : "Speak"}
                  <FontAwesomeIcon icon={isListening ? faStop : faMicrophone} className="ml-1.5" />
                </button>
              </div>

              {isListening && (
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse flex-shrink-0" />
                  <span className="text-xs text-emerald-400 font-medium">Listening — speech will appear in chat</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Right: Chat ── */}
        <div className="flex flex-col h-full overflow-hidden p-3 lg:pl-0">
          <div className="bg-[#111d42] border border-white/10 rounded-xl flex flex-col h-full overflow-hidden">
            {/* Chat header */}
            <div className="border-b border-white/10 px-4 py-3 flex-shrink-0">
              <h2 className="text-white text-sm font-semibold">Live Transcript</h2>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3">
              {messages?.data?.map((msg, index) => {
                const messageKey = msg.id || msg.tempId || `${msg.userId || 'ai'}-${index}-${msg.message?.substring(0, 20)}`;
                return (
                  <div key={messageKey} className={`flex items-start gap-2 ${msg?.userId ? "justify-end" : ""}`}>
                    {!msg.userId && (
                      <div className="w-7 h-7 rounded-lg bg-[#1B2B5E] border border-white/10 flex items-center justify-center flex-shrink-0">
                        <RiRobot2Fill className="text-[#F05A22] text-xs" />
                      </div>
                    )}
                    <div
                      ref={index === messages.data.length - 1 ? containerRef : null}
                      className={`max-w-[80%] p-3 rounded-2xl text-sm leading-relaxed ${
                        msg.userId
                          ? "bg-[#1B2B5E] text-white rounded-tr-sm border border-white/10"
                          : "bg-[#162035] border border-white/10 text-white/80 rounded-tl-sm"
                      }`}
                    >
                      {parseMessageWithDownloads(msg?.message).map((part, partIndex) => {
                        if (part.type === "text") {
                          return (
                            <span
                              key={`${messageKey}-part-${partIndex}`}
                              className="msg-main-box"
                              dangerouslySetInnerHTML={{ __html: marked(part.content) }}
                            />
                          );
                        } else if (part.type === "download") {
                          return (
                            <button
                              key={`${messageKey}-download-${partIndex}`}
                              onClick={() => handleDownload(part.content, `download-${index}-${partIndex}.pdf`)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#F05A22] text-white text-xs font-semibold rounded-lg hover:bg-[#c8471a] transition-colors mt-2"
                            >
                              Download PDF
                            </button>
                          );
                        }
                        return null;
                      })}
                    </div>
                    {msg.userId && (
                      <div className="w-7 h-7 rounded-lg bg-[#1e2d4a] border border-white/10 flex items-center justify-center flex-shrink-0">
                        <RiUserFill className="text-white/50 text-xs" />
                      </div>
                    )}
                  </div>
                );
              })}

              {loading && (
                <div className="flex items-start gap-2">
                  <div className="w-7 h-7 rounded-lg bg-[#1B2B5E] border border-white/10 flex items-center justify-center flex-shrink-0">
                    <RiRobot2Fill className="text-[#F05A22] text-xs" />
                  </div>
                  <div className="bg-[#162035] border border-white/10 p-3 rounded-2xl rounded-tl-sm">
                    <div className="flex items-center gap-2 text-sm text-white/50">
                      <FaSpinner className="animate-spin text-[#F05A22]" />
                      <span>Thinking...</span>
                    </div>
                  </div>
                </div>
              )}

              {isGeneratingPdf && (
                <div className="absolute inset-0 bg-[#0f1829]/90 flex flex-col items-center justify-center z-10">
                  <div className="w-10 h-10 border-4 border-white/10 border-t-[#F05A22] rounded-full animate-spin mb-3" />
                  <p className="text-white/60 text-sm">Generating PDF...</p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="border-t border-white/10 bg-[#0f1829] p-3 flex-shrink-0">
              <div className="relative">
                {imageBase64 && (
                  <div className="mb-2 relative inline-block">
                    <div className="rounded-lg overflow-hidden border border-white/10">
                      {renderFilePreview(imageBase64, fileType, fileName)}
                    </div>
                    <button
                      onClick={clearFileAttachment}
                      className="absolute top-1 right-1 bg-[#1B2B5E] text-white rounded-full p-1 hover:bg-[#F05A22] transition-colors"
                      disabled={speaking}
                    >
                      <FaTimes size={12} />
                    </button>
                  </div>
                )}
                <textarea
                  value={messageFromChat}
                  onChange={(e) => setMessageFromChat(e.target.value)}
                  placeholder={speaking ? "Anita is speaking..." : isListening ? "Listening — speak now..." : "Type your response..."}
                  className={`w-full p-3 pr-24 border border-white/10 bg-[#162035] text-white placeholder-white/25 rounded-xl resize-none focus:outline-none focus:border-[#F05A22]/40 focus:ring-1 focus:ring-[#F05A22]/20 transition-all text-sm ${
                    speaking ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  rows={2}
                  disabled={speaking}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && !speaking && !isListening) {
                      e.preventDefault();
                      if (messageFromChat.trim()) sendMesageFromChat(messageFromChat);
                    }
                  }}
                />
                <div className="absolute right-3 bottom-2.5 flex items-center gap-1">
                  <label
                    htmlFor="file-upload-anita"
                    className={`p-1 text-white/30 hover:text-[#F05A22] transition-colors ${speaking ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <AiOutlinePaperClip className="text-xl" />
                    <input id="file-upload-anita" type="file" className="hidden" onChange={handleFile} disabled={speaking} />
                  </label>
                  <button
                    onClick={() => { if (messageFromChat.trim() && !speaking && !isListening) sendMesageFromChat(messageFromChat); }}
                    disabled={!messageFromChat.trim() || speaking || isListening}
                    className={`p-2.5 rounded-lg transition-all ${
                      messageFromChat.trim() && !speaking && !isListening
                        ? "bg-[#F05A22] text-white hover:bg-[#c8471a]"
                        : "bg-white/5 text-white/20 cursor-not-allowed"
                    }`}
                  >
                    <FaPaperPlane className="text-sm" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatVoiceInterview;

// import { faMicrophone } from "@fortawesome/free-solid-svg-icons";
// import UseAnitaChat from "../utils/use-anita-chat";
// import UseAuth from "../utils/use-auth";
// import axiosInstance from "../utils/axios-instance";
// import Speak from "../utils/speak";
// import { FaPaperPlane, FaSpinner, FaTimes, FaFilePdf, FaFileWord } from "react-icons/fa";
// import { AiOutlinePaperClip } from "react-icons/ai";
// import { RiRobot2Fill, RiUserFill } from "react-icons/ri";
// import { jsPDF } from 'jspdf';
// import { marked } from "marked";
// import { ExtractDocumentText } from "../utils/extract-document-text";

// const ChatVoiceInterview = () => {
//   const { user } = UseAuth();
//   const { currentConversation, handleSelectConversation } = UseAnitaChat();
//   const [messages, setMessages] = useState({ totalCount: 0, data: [] });
//   const [loading, setLoading] = useState(false);
//   const [speaking, setSpeaking] = useState(false);
//   const [recording, setRecording] = useState(false);
//   const [transcript, setTranscript] = useState("");
//   const recognitionRef = useRef(null);
//   const [timeLeft, setTimeLeft] = useState(900); // 15 minutes (900 seconds)
//   const [interviewActive, setInterviewActive] = useState(false);
//   const [messageFromChat, setMessageFromChat] = useState("");
//   const messagesEndRef = useRef(null);
//   const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
//   const containerRef = useRef(null);
//   // Added flag to check if interview has been started
//   const [interviewStarted, setInterviewStarted] = useState(false);

//   const [loadingState, setLoadingState] = useState(null);
//   const[imageBase64, setImageBase64]  = useState(null);
//   const [extractedText, setExtractedText] = useState(null);
//   const [fileType, setFileType] = useState(null); // Track file type: "image", "pdf", "doc"
//   const [fileName, setFileName] = useState(""); // Track file name

//   // Store conversation ID in ref to ensure it persists between renders
//   // and doesn't trigger re-renders when updated
//   const conversationIdRef = useRef(null);

//   // Keep track of previous conversation ID to detect changes
//   const prevConversationIdRef = useRef(currentConversation?.id);

//   // ------------------------------------------- DETECT CONVERSATION RESET -------------------------------------------
//   useEffect(() => {
//     // Check if conversation was set to null (reset triggered from sidebar)
//     const wasResetTriggered =
//       prevConversationIdRef.current && !currentConversation?.id;

//     if (wasResetTriggered) {
//       // console.log("Conversation reset detected! Resetting all states...");

//       // Reset all component states
//       setMessages({ totalCount: 0, data: [] });
//       setLoading(false);
//       setSpeaking(false);
//       setRecording(false);
//       setTranscript("");
//       setTimeLeft(900);
//       setInterviewActive(false);
//       setMessageFromChat("");
//       setInterviewStarted(false);

//       // Clear the conversation ID reference
//       conversationIdRef.current = null;

//       // Clear the loaded conversations set
//       if (loadedConversationsRef.current) {
//         loadedConversationsRef.current.clear();
//       }

//       // If speech recognition is running, stop it
//       if (recognitionRef.current && recording) {
//         recognitionRef.current.abort();
//       }
//     }

//     // Check if conversation ID changed (user selected a different conversation)
//     if (prevConversationIdRef.current !== currentConversation?.id && currentConversation?.id) {
//       // console.log("Conversation changed! Updating messages...");
//       // We'll fetch messages for the new conversation
//       // Make sure we don't have stale messages displayed while fetching
//       setMessages({ totalCount: 0, data: [] });
//     }

//     // Update the previous conversation ID reference for next comparison
//     prevConversationIdRef.current = currentConversation?.id;
//   }, [currentConversation, recording]);

//   // Auto-scroll to bottom of messages
//   useEffect(() => {
//     messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
//   }, [messages]);

//   // ------------------------------------------- USE EFFECT FOR MESSAGES -------------------------------------------
//   // Keep track of whether we've loaded messages for this conversation
//   const loadedConversationsRef = useRef(new Set());

//   useEffect(() => {
//     const findMessages = async () => {
//       try {
//         // Always use the ref first, then fall back to context
//         const conversationId =
//           conversationIdRef.current || currentConversation?.id;

//         if (!conversationId) {
//           // console.log("No conversation ID available, not fetching messages");
//           return;
//         }

//         // Check if we've already loaded messages for this conversation
//         if (loadedConversationsRef.current.has(conversationId)) {
//           // console.log(`Messages already loaded for conversation: ${conversationId}`);
//           return;
//         }

//         const res = await axiosInstance(
//           `/voice-chat/find/messages/${conversationId}`
//         );

//         // Set messages without duplicating
//         setMessages(res.data);

//         // Mark this conversation as loaded
//         loadedConversationsRef.current.add(conversationId);
//       } catch (error) {
//         // console.log("Error fetching messages:", error);
//       }
//     };

//     // Only fetch if we have a conversation ID
//     if (conversationIdRef.current || currentConversation?.id) {
//       findMessages();
//     }
//   }, [currentConversation?.id]);


//   //-------------PARSE MESSAGE TO DOWNLOAD -------------------------
//   const parseMessageWithDownloads = (message) => {
//     // Regular expression to match content between download start and end flags
//     const downloadPattern = /--download start\s*([\s\S]*?)\s*--download end/g;


//     let result = [];
//     let lastIndex = 0;
//     let match;

//     // Find all download sections
//     while ((match = downloadPattern.exec(message)) !== null) {
//       // Add text before the download section
//       if (match.index > lastIndex) {
//         result.push({
//           type: 'text',
//           content: message.substring(lastIndex, match.index)
//         });
//       }

//       // Add the download section
//       result.push({
//         type: 'download',
//         content: match[1].trim()
//       });

//       lastIndex = match.index + match[0].length;
//     }

//     // Add any remaining text after the last download section
//     if (lastIndex < message.length) {
//       result.push({
//         type: 'text',
//         content: message.substring(lastIndex)
//       });
//     }

//     return result;
//   };

//   // Function to generate and download PDF
//   const handleDownload = (content, filename = 'download.pdf') => {
//     // Create new PDF document
//     const doc = new jsPDF();

//     // Add the content to the PDF
//     doc.text(content, 10, 10);

//     // Save the PDF
//     doc.save(filename);
//   };

//   const filterTextFromDownloadbaleContent = (input) => {
//     //this is implemented so the voice asistant does not read the downloadable content
//     return input.replace(/--download start[\s\S]*?--download end/g, '').trim();
//   }

//   // ------------------------------------------- START CONVERSATION -------------------------------------------
//   const startConversation = async () => {
//     if (!speaking && !interviewStarted) {
//       setSpeaking(true);
//       setInterviewActive(true);
//       setInterviewStarted(true);

//       // This message has download content: --download start \nThis is important information that will be downloaded as a PDF file.\n--download end
//       const introMessage = `Hello, Welcome to the Martech Interview Simulator We are here to simulate real-life interview experiences for marketing technology roles. Before we begin ✅ Find a quiet, comfortable space. ✅ Use a headset or speak clearly for better communication. Ready to begin? Just say Proceed, and we will start your Interview. 
//       `;

//       // Generate unique message ID to prevent duplicates
//       const introMessageId = 'intro-' + Date.now();

//       // Check if intro message already exists to prevent duplication
//       setMessages((prevMessages) => {
//         // Check if an intro message already exists
//         const introExists = prevMessages.data.some(
//           msg => msg.userId === null && msg.message.includes("Welcome to the Martech Interview Simulator")
//         );

//         if (introExists) {
//           return prevMessages;
//         }


//         return {
//           totalCount: prevMessages.totalCount + 1,
//           data: [...prevMessages.data, {
//             message: introMessage,
//             userId: null,
//             id: introMessageId
//           }],
//         };
//       });

//       const filteredIntroMessage = filterTextFromDownloadbaleContent(introMessage);

//       await Speak(filteredIntroMessage, () => {
//         setSpeaking(false);
//         toggleRecording(); // Start listening for the user's response
//       });
//     }
//   };


//   // ------------------------------------------- SEND MESSAGE -------------------------------------------
//   const sendMesageFromChat = (msg) => {
//     sendMessage(msg);
//     setMessageFromChat("");
//   };

//   const sendMessage = async (msg) => {
//     if (!msg.trim()) return;

//     setLoading(true);

//     // Always send the message with the ref's conversation ID if available
//     const message = {
//       conversationId: conversationIdRef.current || currentConversation?.id,
//       message: msg,
//       userId: user?.id,
//     };

//     // Generate a temporary ID for this message to prevent duplicates
//     const tempMessageId = Date.now().toString();
//     message.tempId = tempMessageId;

//     // Add user message to UI immediately
//     setMessages((prevMessages) => {
//       // Check if this message already exists (by content and user) to avoid duplicates
//       // const isDuplicate = prevMessages.data.some(
//       //   m => m.message === message.message && m.userId === message.userId && !m.isAI
//       // );

//       // if (isDuplicate) {
//       //   return prevMessages;
//       // }

//       return {
//         totalCount: prevMessages.totalCount + 1,
//         data: [...prevMessages.data, message],
//       };
//     });

//         setFileType(null); // Clear file type
//         setFileName(""); // Clear file name
//         setExtractedText(null)
//         setImageBase64(null); // Clear file preview

//     try {
//       // Send the message to the API
//       const res = await axiosInstance.post(`/voice-chat/send/message`, {
//         ...message,
//         messages: messages?.data,
//         extraPrompt: extractedText ? `${extractedText}\n\n${msg}` : null
//       });

      
//       // Check if this is our first message (no conversation ID yet)
//       if (
//         !conversationIdRef.current &&
//         res.data.conversation &&
//         res.data.conversation.id
//       ) {
//         // Store the conversation ID in our ref
//         conversationIdRef.current = res.data.conversation.id;

//         // Also update the context if the handleSelectConversation function is available
//         if (handleSelectConversation) {
//           handleSelectConversation(res.data.conversation);
//         }
//       }

//       // Start speaking the response
//       setSpeaking(true);
//       // const filteredResponse = filterTextFromDownloadbaleContent(res?.data?.message?.message);  
//       await Speak(res?.data?.message?.message, afterSpeach);

//       // Add AI response to messages, avoiding duplicates
//       setMessages((prev) => {
//         // Check if this AI response already exists to avoid duplicates
//         const isDuplicate = prev.data.some(
//           m => m.message === res.data.message.message && m.userId === null
//         );


//         if (isDuplicate) {
//           return prev;
//         }

//         return {
//           totalCount: prev.totalCount + 1,
//           data: [...prev.data, res.data.message],
//         };
//       });
//     } catch (error) {
//       console.error("Error sending message:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const afterSpeach = () => {
//     setSpeaking(false);
//     toggleRecording();
//   };

//   //   ----------------------------------------- RECORDING -----------------------------------------
//   // useEffect(() => {
//   //   const SpeechRecognition =
//   //     window.SpeechRecognition || window.webkitSpeechRecognition;

//   //   if (SpeechRecognition) {
//   //     recognitionRef.current = new SpeechRecognition();
//   //     recognitionRef.current.continuous = false;
//   //     recognitionRef.current.interimResults = false;
//   //     recognitionRef.current.onresult = (event) => {
//   //       const lastResultIndex = event.results.length - 1;
//   //       const lastResult = event.results[lastResultIndex];
//   //       if (lastResult.isFinal) {
//   //         let transScript = lastResult[0].transcript;
//   //         // sendMessage(transScript); //commented out so it doesn't send message automatically 
//   //         setMessageFromChat(transScript)
//   //         setTranscript(transScript);
//   //       }
//   //     };

//   //     recognitionRef.current.onend = () => {
//   //       setRecording(false);
//   //     };
//   //   }
//   // }, [recording]);


//   useEffect(() => {
//   const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

//   if (!SpeechRecognition) return;

//   recognitionRef.current = new SpeechRecognition();
//   recognitionRef.current.continuous = false;
//   recognitionRef.current.interimResults = false;

//   recognitionRef.current.onresult = (event) => {
//     const lastResult = event.results[event.results.length - 1];
//     if (lastResult.isFinal) {
//       const transScript = lastResult[0].transcript;
//       let i = 0;

//       const type = () => {
//         if (i <= transScript.length) {
//           setMessageFromChat(transScript.slice(0, i));
//           i++;
//           setTimeout(type, 50); // adjust typing speed here
//         }
//       };

//       type();
//       setTranscript(transScript);
//     }
//   };

//   recognitionRef.current.onend = () => setRecording(false);
// }, [recording]);


//   // ------------------------------------------ TOGGLE INTERVIEW --------------------------------
//   const toggleRecording = () => {
//     if (recognitionRef.current) {
//       if (!recording && !speaking) { // Added !speaking check to prevent recording while speaking
//         setRecording(true);
//         recognitionRef.current.start();
//       } else {
//         setRecording(false);
//         recognitionRef.current.abort();
//       }
//     }
//   };

//   const handleFile = async (e) => {
//     // Prevent file handling when speaking
//     if (speaking) {
//       return;
//     }
    
//     const file = e.target.files[0];

//     if (!file) return;

//     // Validate file type
//     const validTypes = [
//       'application/pdf', // PDF
//       'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' // DOC, DOCX
//     ];

//     if (!validTypes.includes(file.type)) {
//       alert("Only PDF, DOC, and DOCX files are allowed.");
//       return;
//     }

//     setFileName(file.name);

//   if (file.type === 'application/pdf') {
//       // Handle PDF files
//       setFileType("pdf");
//       setLoadingState("processing-pdf");

//       // Create a preview icon for PDF
//       setImageBase64("pdf"); // Using a marker to indicate it's a PDF file

//       // Extract text from PDF
//       const text = await extractPdfText(file);
//       setLoadingState(null);
//       setExtractedText(text)

//       // Send the extracted text
//       // await handleSendMessage(text);
//     }
//     else if (file.type === 'application/msword' ||
//       file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
//       // Handle DOC/DOCX files
//       setFileType("doc");
//       setLoadingState("processing-document");

//       // Create a preview icon for DOC/DOCX
//       setImageBase64("doc"); // Using a marker to indicate it's a DOC file

//       // Use existing document text extraction function
//       const text = await ExtractDocumentText(file);
//       setLoadingState(null);
//       setExtractedText(text)
//     }
//   };

//   const extractPdfText = async (file) => {
//     try {
//       // Use any PDF extraction library or API you have access to
//       // For this example, we'll assume ExtractDocumentText can handle PDFs
//       const text = await ExtractDocumentText(file);
//       return text;
//     } catch (error) {
//       console.error("Error extracting PDF text:", error);
//       return "Error extracting text from PDF.";
//     }
//   };

//   // Clear file attachment
//   const clearFileAttachment = () => {
//     setImageBase64(null);
//     setFileType(null);
//     setFileName("");
//     setExtractedText(null);
//   };

//   // Render file preview based on file type
//   const renderFilePreview = (fileData, fileType, name) => {
//     if (fileType === "image" || !fileType) {
//       // For backward compatibility with existing image previews
//       return (
//         <img src={fileData} alt="Preview" className="max-h-36 object-contain" />
//       );
//     } else if (fileType === "pdf" || fileData === "pdf") {
//       return (
//         <div className="flex items-center p-2 bg-red-50 rounded-md">
//           <FaFilePdf className="text-red-500 text-2xl mr-2" />
//           <span className="text-sm truncate max-w-[150px]">{name || "PDF Document"}</span>
//         </div>
//       );
//     } else if (fileType === "doc" || fileData === "doc") {
//       return (
//         <div className="flex items-center p-2 bg-blue-50 rounded-md">
//           <FaFileWord className="text-blue-500 text-2xl mr-2" />
//           <span className="text-sm truncate max-w-[150px]">{name || "Word Document"}</span>
//         </div>
//       );
//     }
//     return null;
//   };


//   return (
//     <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
//       {/* Header - Fixed height */}
//       <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 sm:p-4 flex-shrink-0">
//         <h2 className="ps-4 lg:ps-0 text-xl sm:text-2xl font-semibold text-brandPrimary dark:text-white">Anita</h2>
//         <p className="ps-4 lg:ps-0 text-xs sm:text-sm text-gray-500 dark:text-gray-400">Martech Interview Simulator</p>
//       </div>

//       {/* Main content - Using grid for better control on small screens */}
//       <div className="flex-1 grid grid-rows-[auto_1fr] lg:grid-rows-1 lg:grid-cols-[30%_70%] overflow-hidden">
//         {/* Video/Control section - Fixed on top for mobile, side column for desktop */}
//         <div className="w-full flex-shrink-0 overflow-visible p-2 lg:h-full">
//           <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm dark:shadow-gray-900/30 flex flex-col h-auto lg:h-full">
//             {/* Video container with responsive height */}
//             <div className="w-full h-36 sm:h-40 md:h-48 lg:h-[70%] rounded-md overflow-hidden p-2">
//               {speaking ? (
//                 <div className="w-full h-full flex items-center justify-center relative">
//                   <video
//                     src={Aivideo}
//                     className="absolute inset-0 w-full h-full object-cover"
//                     loop
//                     autoPlay
//                     muted
//                   />
//                 </div>
//               ) : (
//                 <div className="w-full h-full flex items-center justify-center">
//                   <img
//                     src={aiImg}
//                     className="w-full h-full object-contain"
//                     alt="AI assistant"
//                   />
//                 </div>
//               )}
//             </div>

//             {/* Controls - Always visible with reduced height on mobile */}
//             <div className="w-full bg-white dark:bg-gray-800 rounded-md p-2 flex-shrink-0 border-t border-gray-200 dark:border-gray-700">
//               <div className="w-full flex flex-row items-center justify-center space-x-2">
//                 <button
//                   className="w-1/2 py-2 px-2 rounded-lg bg-brandPrimary text-white cursor-pointer disabled:opacity-[0.7] hover:bg-blue-900 transition-colors text-sm"
//                   onClick={startConversation}
//                   disabled={speaking || recording || interviewStarted}
//                 >
//                   {interviewStarted ? "Started" : "Start"}
//                 </button>
//                 <button
//                   className="w-1/2 py-2 px-2 rounded-lg bg-brandPrimary text-white cursor-pointer disabled:opacity-[0.7] hover:bg-blue-900 transition-colors text-sm"
//                   onClick={toggleRecording}
//                   disabled={loading || speaking || !interviewStarted}
//                 >
//                   {loading
//                     ? "Wait"
//                     : speaking
//                       ? "Speak"
//                       : recording
//                         ? "Listen"
//                         : "Speak"}
//                   <FontAwesomeIcon icon={faMicrophone} className="ml-1" />
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Chat section - Flexible height with fixed header and input area */}
//         <div className="w-full flex flex-col h-full overflow-hidden p-2 lg:pl-0">
//           <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm rounded-lg flex flex-col h-full">
//             {/* Chat header - Fixed height */}
//             <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 px-3 py-3 flex-shrink-0">
//               <div className="flex-shrink-0">
//                 <h2 className="text-brandPrimary dark:text-white text-base sm:text-lg font-semibold">
//                   Live Summary
//                 </h2>
//               </div>
//             </div>

//             {/* Message area - Flexible height, scrollable */}
//             <div className="flex-1 overflow-y-auto p-2 sm:p-4 space-y-3 sm:space-y-4 dark:bg-gray-800">
//               {messages?.data?.map((msg, index) => {
//                 const messageKey = msg.id || msg.tempId || `${msg.userId || 'ai'}-${index}-${msg.message?.substring(0, 20)}`;

//                 return (
//                   <div
//                     key={messageKey}
//                     className={`flex items-start space-x-2 ${msg?.userId ? "justify-end" : ""}`}
//                   >
//                     {!msg.userId && (
//                       <div className="bg-blue-100 dark:bg-blue-900/30 p-1 sm:p-2 rounded-full text-base sm:text-xl flex-shrink-0">
//                         <RiRobot2Fill className="text-brandPrimary dark:text-blue-400" />
//                       </div>
//                     )}

//                     <div
//                       ref={index === messages.data.length - 1 ? containerRef : null}
//                       className={`max-w-[80%] ${msg.userId
//                         ? "bg-brandPrimary text-white"
//                         : "bg-white dark:bg-gray-700 text-gray-800 dark:text-white border border-gray-200 dark:border-gray-600"
//                         } p-2 sm:p-3 rounded-lg shadow-sm text-sm sm:text-base`}
//                     >
//                       {parseMessageWithDownloads(msg?.message).map((part, partIndex) => {
//                         if (part.type === "text") {
//                           return (
//                             <span
//                               key={`${messageKey}-part-${partIndex}`}
//                               dangerouslySetInnerHTML={{ __html: marked(part.content) }}
//                             />
//                           );
//                         } else if (part.type === "download") {
//                           return (
//                             <button
//                               key={`${messageKey}-download-${partIndex}`}
//                               onClick={() => handleDownload(part.content, `download-${index}-${partIndex}.pdf`)}
//                               className="inline-flex items-center px-2 sm:px-4 py-1 sm:py-2 bg-blue-600 text-white text-xs sm:text-sm font-medium rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition mt-2"
//                             >
//                               Download PDF
//                             </button>
//                           );
//                         }
//                         return null;
//                       })}
//                     </div>
//                     {msg.userId && (
//                       <div className="bg-blue-100 dark:bg-blue-900/30 p-1 sm:p-2 rounded-full text-base sm:text-xl flex-shrink-0">
//                         <RiUserFill className="text-brandPrimary dark:text-blue-400" />
//                       </div>
//                     )}
//                   </div>
//                 );
//               })}
//               {loading && (
//                 <div className="flex items-start gap-2 sm:gap-3">
//                   <div className="bg-blue-100 dark:bg-blue-900/30 p-1 sm:p-2 rounded-full text-base sm:text-xl flex-shrink-0">
//                     <RiRobot2Fill className="text-brandPrimary dark:text-blue-400" />
//                   </div>
//                   <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 p-2 sm:p-3 rounded-lg shadow-sm">
//                     <div className="flex items-center gap-2">
//                       <FaSpinner className="animate-spin text-brandPrimary dark:text-blue-400" />
//                       <span className="text-sm sm:text-base dark:text-white">Thinking...</span>
//                     </div>
//                   </div>
//                 </div>
//               )}
//               {/* Loading overlay for PDF generation */}
//               {isGeneratingPdf && (
//                 <div className="absolute inset-0 bg-white dark:bg-gray-900 bg-opacity-90 dark:bg-opacity-90 flex flex-col items-center justify-center z-10">
//                   <div className="w-12 h-12 border-4 border-gray-200 dark:border-gray-700 border-t-green-500 rounded-full animate-spin mb-4"></div>
//                   <p className="text-gray-700 dark:text-gray-300 font-medium">
//                     Generating PDF...
//                   </p>
//                 </div>
//               )}
//               <div ref={messagesEndRef} />
//             </div>

//             {/* Input area - Fixed height */}
//             <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2 sm:p-3 flex-shrink-0">
//               <div className="relative">
//                 {/* File preview for conversation mode */}
//                 {imageBase64 && (
//                   <div className="mb-2 relative inline-block">
//                     <div className="rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
//                       {renderFilePreview(imageBase64, fileType, fileName)}
//                     </div>
//                     <button
//                       onClick={clearFileAttachment}
//                       className="absolute top-1 right-1 bg-gray-800 bg-opacity-70 text-white rounded-full p-1 hover:bg-opacity-90"
//                       disabled={speaking}
//                     >
//                       <FaTimes size={14} />
//                     </button>
//                   </div>
//                 )}
//                 <textarea
//                   value={messageFromChat}
//                   onChange={(e) => setMessageFromChat(e.target.value)}
//                   placeholder={speaking ? "AI is speaking..." : "Type your message..."}
//                   className={`w-full p-2 sm:p-2.5 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm sm:text-base ${speaking ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed' : ''}`}
//                   rows={2}
//                   disabled={speaking}
//                   onKeyDown={(e) => {
//                     if (e.key === "Enter" && !e.shiftKey && !speaking) {
//                       e.preventDefault();
//                       if (messageFromChat.trim()) {
//                         sendMesageFromChat(messageFromChat);
//                       }
//                     }
//                   }}
//                 ></textarea>
//                 <div className="absolute right-2 bottom-1 flex items-center gap-1">
//                   <label
//                     htmlFor="file-upload"
//                     className={`p-1.5 sm:p-2 text-brandPrimary dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 ${speaking ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} z-10`}
//                   >
//                     <AiOutlinePaperClip className="text-lg sm:text-xl" />
//                     <input
//                       id="file-upload"
//                       type="file"
//                       className="hidden"
//                       onChange={handleFile}
//                       disabled={speaking}
//                     />
//                   </label>
//                   <button
//                     onClick={() => {
//                       if (messageFromChat.trim() && !speaking) {
//                         sendMesageFromChat(messageFromChat);
//                       }
//                     }}
//                     disabled={!messageFromChat.trim() || speaking}
//                     className={`p-2 rounded-full ${messageFromChat.trim() && !speaking
//                       ? "bg-brandPrimary text-white hover:bg-blue-900"
//                       : "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
//                       }`}
//                   >
//                     <FaPaperPlane />
//                   </button>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ChatVoiceInterview;